import { CompactText } from 'ui/CompactText';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHead } from 'ui/kit/PageHead';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { api } from 'api/v1/api';
import { bulletins as bulletinPaths, closeBulletin } from 'api/v1/requests/bulletins';
import { useBulletin } from 'hooks/useBulletin';
import { useAuthSession } from 'hooks/useAuthSession';
import { useMyStudentProposals } from 'hooks/useStudentTutorSearch';
import { addNotification } from 'notifications';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import {
  formatBulletinCategoryLine,
  formatBulletinGrades,
  formatBulletinSubjectTitle,
  formatParticipantLine,
  translatePlannedDuration,
  translateStudentType,
  translateStudyLocations,
  translateTrialPreference,
  translateTutorGender,
  formatBulletinSchedule,
} from 'utils/bulletinLabels';
import { studyTimeFromApi } from 'utils/studyTime';

import { LOCATION_MAP, mapTaskFormToRequest } from '../NewTask/comfig';
import { DEFAULT_SUBJECT, useTaskFormStore } from '../NewTask/taskForm.store';
import { TaskWizardModal } from '../NewTask/TaskWizardModal';
import screen from '../StudentTaskScreen.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { DetailsMenuSlot } from 'modules/Details/DetailsMenu/DetailsMenu';

const formatBudget = (from?: number, to?: number) => {
  if (from && to && from !== to) {
    return `${from} - ${to} руб/час`;
  }
  if (from) return `${from} руб/час`;
  if (to) return `${to} руб/час`;
  return '';
};

// Обратное соответствие LOCATION_MAP: online -> «Онлайн» и т.д.
const LOCATION_FORM_VALUE: Record<string, string> = Object.fromEntries(
  Object.entries(LOCATION_MAP).map(([label, value]) => [value, label]),
);

const updateBulletinFields = async (id: number, payload: Record<string, unknown>) => {
  const res = await api.put(bulletinPaths.update(id), payload);
  return res.data;
};

const statusLabel = (closed?: boolean, verification?: string, active?: boolean) => {
  if (closed) return 'Заявка закрыта';
  if (verification === 'pending_review') return 'На модерации';
  if (verification === 'rejected') return 'Отклонена модератором';
  if (verification === 'changes_requested') return 'Нужны правки модератора';
  if (active) return 'Отклики на задачу включены';
  return 'Отклики на задачу отключены';
};

export const DetailsTaskNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bulletinId = parsePositiveInt(id) ?? 0;
  const { data: bulletin, isLoading } = useBulletin(bulletinId);
  const { data: proposals } = useMyStudentProposals();
  const { data: authSession } = useAuthSession();
  // Отклики репетиторов по задаче (автор): цена/формат, согласованные в чате.
  const { data: responses } = useQuery({
    queryKey: ['bulletin-responses', bulletinId],
    // Только автору: чужому ученику API отвечает 403 (и глобальный тост «Только автор…»).
    enabled:
      bulletinId > 0 &&
      Boolean(bulletin) &&
      (bulletin as { id_user?: number } | undefined)?.id_user ===
        (authSession as { id?: number } | null | undefined)?.id,
    queryFn: async () =>
      (await api.get(`/bulletins/${bulletinId}/responses`)).data as Array<{
        status?: string;
        chat_id?: number | null;
        tutor_id?: number;
        tutor_name?: string;
        proposed_price?: number | null;
        agreement?: { price?: number | null; format?: string | null } | null;
      }>,
  });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // Редактирование: мастер работает с общим хранилищем черновика новой задачи.
  // Перед открытием кладём туда поля этой задачи, а черновик откладываем и
  // возвращаем после закрытия, чтобы правка не затирала начатую новую задачу.
  const [editPending, setEditPending] = useState<Record<string, unknown> | null>(null);
  const draftBeforeEditRef = useRef<Record<string, unknown> | null>(null);
  const editSnapshotRef = useRef('');

  const saveEdit = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = mapTaskFormToRequest(values, {
        registrant_type: values.isSelf === false ? 'parent' : 'self',
      });
      if (!payload.price_to) payload.price_to = null;
      return updateBulletinFields(bulletinId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bulletin', bulletinId] });
      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      setEditPending(null);
      addNotification('Изменения в задаче сохранены', 'success');
    },
    onError: () => {
      addNotification('Не удалось сохранить изменения. Попробуй еще раз.', 'error');
    },
  });

  const closeTask = useMutation({
    mutationFn: () => closeBulletin(bulletinId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bulletin', bulletinId] });
      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      setConfirmClose(false);
      addNotification('Поиск по задаче завершен', 'success');
    },
    onError: () => {
      addNotification('Не удалось завершить задачу. Попробуй еще раз.', 'error');
    },
  });

  if (isLoading) {
    return <BrandLoader center label='Загрузка...' />;
  }

  if (!bulletin) {
    return <p>Задача не найдена</p>;
  }

  // Чужая задача по подстановке id: API отдаёт одобренные заявки всем вошедшим (для репетиторов),
  // но экран ученика - только для автора (раньше показывался интерфейс владельца с чужими данными).
  const ownerId = (bulletin as { id_user?: number }).id_user;
  const meId = (authSession as { id?: number } | null | undefined)?.id;
  if (ownerId && meId && ownerId !== meId) {
    return (
      <main className={screen.page} data-foreign-task>
        <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>Задача недоступна</>} />
        <section className={screen.card}>
          <p className={screen.body}>Это задача другого ученика. Откройте свои задачи в разделе «Мои задачи».</p>
          <div className={screen.actions}>
            <button type='button' className={screen.ink} onClick={() => navigate('/profile/tasks')}>
              К моим задачам
            </button>
          </div>
        </section>
      </main>
    );
  }

  const subjectTitle = formatBulletinSubjectTitle(bulletin.subject_category) || 'Задача';
  const subjectLine = formatBulletinCategoryLine(bulletin.subject_category);
  const grades = formatBulletinGrades(bulletin);
  const locations = translateStudyLocations(bulletin.study_locations);
  const budget = formatBudget(bulletin.price_from, bulletin.price_to);
  const duration = translatePlannedDuration(bulletin.planned_duration);
  const schedule = formatBulletinSchedule(bulletin);
  const closed = Boolean(bulletin.closed);
  const mine = (proposals || []).filter((item) => item.bulletin_id === bulletin.id);

  const openEdit = () => {
    const store = useTaskFormStore.getState();
    draftBeforeEditRef.current = store.values;
    // subject_category в ответе API - объект {category, subcategory, *_ru}
    const rawCategory = bulletin.subject_category as unknown;
    const category =
      rawCategory && typeof rawCategory === 'object'
        ? (rawCategory as {
            category?: string;
            subcategory?: string;
            category_ru?: string;
            subcategory_ru?: string;
          })
        : { category: typeof rawCategory === 'string' ? rawCategory : '' };
    const values: Record<string, unknown> = {
      selectedParticipants: [],
      studentType: bulletin.student_type || '',
      grades: (bulletin.grades || []).map((g) => String(g)),
      studentName: bulletin.student_name || '',
      isSelf: bulletin.self_study ?? true,
      teacherGender: bulletin.tutor_gender_preference || 'any',
      duration: bulletin.planned_duration || null,
      locations: (bulletin.study_locations || [])
        .map((loc) => LOCATION_FORM_VALUE[loc])
        .filter(Boolean),
      comment: bulletin.wishes || '',
      // Старые заявки без study_time: предзаполняем из кодов (будни -> Пн-Пт, вечер -> 17:00-21:00).
      studyTime: studyTimeFromApi(bulletin.study_time, bulletin.study_schedule),
      // «Гибко» или время не указано: шаг можно оставить пустым, старые коды не трогаем.
      studyTimeOptional: !studyTimeFromApi(bulletin.study_time, bulletin.study_schedule).days.length,
      priceFrom: bulletin.price_from ?? 500,
      priceTo: bulletin.price_to ?? null,
      trial: bulletin.trial_lesson_preference || null,
      subject: category.category || DEFAULT_SUBJECT,
      subjectLabel: category.category_ru || 'Математика',
      goal: category.subcategory || '',
      goalLabel: category.subcategory_ru || '',
      preferredTutorProfileId: null,
    };
    editSnapshotRef.current = JSON.stringify(values);
    store.setValues(values);
    setWizardOpen(true);
  };

  const closeEdit = () => {
    setWizardOpen(false);
    const edited = useTaskFormStore.getState().values;
    if (JSON.stringify(edited) !== editSnapshotRef.current) {
      setEditPending(edited);
    }
    if (draftBeforeEditRef.current) {
      useTaskFormStore.getState().setValues(draftBeforeEditRef.current);
      draftBeforeEditRef.current = null;
    }
  };
  // Сделка: принятый отклик репетитора с чатом (или приглашение ученика с чатом).
  const dealResponse =
    (responses || []).find((r) => r.status === 'accepted' && r.chat_id) ||
    (responses || []).find((r) => r.chat_id);
  const invited = mine.find((item) => item.chat_id);
  const chatProposal = dealResponse
    ? { tutor_id: dealResponse.tutor_id, chat_id: dealResponse.chat_id }
    : invited;
  // Отклики: и отклики репетиторов, и приглашения ученика (раньше считались только приглашения -
  // при принятом отклике было «Пока нет откликов»).
  const RESPONSE_STATUS: Record<string, string> = {
    pending: 'ждёт ответа',
    accepted: 'принят, идёт переписка',
    rejected: 'отклонён',
    completed: 'завершён',
  };
  const plural = (n: number, one: string, few: string, many: string) =>
    n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many;
  const respondedTutors = new Set((responses || []).map((r) => r.tutor_id));
  const responseLines = [
    ...(responses || []).map(
      (r) => `${r.tutor_name || 'Репетитор'} - ${RESPONSE_STATUS[r.status || ''] || r.status || ''}`,
    ),
    ...mine
      .filter((item) => !respondedTutors.has(item.tutor_id))
      .map((item) => `Приглашение репетитору - ${RESPONSE_STATUS[item.status] || item.status}`),
  ];
  // Условия чата: только то, что согласовано с репетитором и отличается от заявки (данные заявки - в карточке выше).
  const fmtRub = (n: number) => `${new Intl.NumberFormat('ru-RU').format(n)} руб/час`;
  const agreement = dealResponse?.agreement || null;
  const agreedPrice = Number(agreement?.price || dealResponse?.proposed_price || 0);
  const chatTerms: Array<{ label: string; value: string }> = [];
  if (dealResponse?.tutor_name) chatTerms.push({ label: 'Преподаватель', value: dealResponse.tutor_name });
  if (agreedPrice > 0) chatTerms.push({ label: 'Цена преподавателя', value: fmtRub(agreedPrice) });
  const agreedFormat = agreement?.format ? translateStudyLocations([agreement.format]) : '';
  if (agreedFormat && agreedFormat !== locations) chatTerms.push({ label: 'Формат', value: agreedFormat });

  return (
    <main className={screen.page}>
      <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>{closed ? 'Задача завершена' : subjectTitle}</>} />
      <DetailsMenuSlot />

      {closed ? (
        <section className={screen.card}>
          <h2 className={screen.cardTitle}>Поиск по задаче завершен</h2>
          <p className={screen.badge}>{statusLabel(true)}</p>
          <p className={screen.body}>
            {[subjectTitle, grades, locations].filter(Boolean).join(' / ')}
          </p>
          <p className={screen.body}>
            Задача закрыта. Это не подтверждает проведение или оплату занятия.
          </p>
          <CompactText className={screen.note} tone={'info'}>
            Для новой цели создай задачу. Договоренность с преподавателем проверь в чате.
          </CompactText>
          <div className={screen.actions}>
            <button type='button' className={screen.ink} onClick={() => navigate('/profile/tasks')}>
              К списку задач
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className={screen.card}>
            <h2 className={screen.cardTitle}>Задача: {subjectTitle.toLowerCase()}</h2>
            <p className={screen.badge}>
              {statusLabel(false, bulletin.verification_status, bulletin.active)}
            </p>
            <p className={screen.body}>{bulletin.wishes?.trim() || subjectLine || subjectTitle}</p>
            <div className={screen.row}>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Класс и цель</p>
                <p className={screen.fieldValue}>
                  {[grades, subjectTitle].filter(Boolean).join(' / ') || 'Не заполнено'}
                </p>
              </div>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Бюджет</p>
                <p className={screen.fieldValue}>{budget || 'Не заполнено'}</p>
              </div>
            </div>
            <div className={screen.row}>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Формат</p>
                <p className={screen.fieldValue}>{locations || 'Не заполнено'}</p>
              </div>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Удобное время</p>
                <p className={screen.fieldValue}>{schedule || 'Не заполнено'}</p>
              </div>
            </div>
            <div className={screen.row}>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Срок занятий</p>
                <p className={screen.fieldValue}>{duration || 'Не заполнено'}</p>
              </div>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Для кого</p>
                <p className={screen.fieldValue}>
                  {[
                    translateStudentType(bulletin.student_type),
                    bulletin.self_study === false ? 'занимается ребёнок' : '',
                  ]
                    .filter(Boolean)
                    .join(', ') || 'Не заполнено'}
                </p>
              </div>
            </div>
            <div className={screen.row}>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Имя ученика</p>
                <p className={screen.fieldValue}>{bulletin.student_name || 'Не заполнено'}</p>
              </div>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Пол репетитора</p>
                <p className={screen.fieldValue}>
                  {translateTutorGender(bulletin.tutor_gender_preference) || 'Не важно'}
                </p>
              </div>
            </div>
            <div className={screen.row}>
              <div className={screen.field}>
                <p className={screen.fieldLabel}>Пробное занятие</p>
                <p className={screen.fieldValue}>
                  {translateTrialPreference(bulletin.trial_lesson_preference) || 'Не заполнено'}
                </p>
              </div>
              {bulletin.participants?.length ? (
                <div className={screen.field}>
                  <p className={screen.fieldLabel}>Участники</p>
                  <p className={screen.fieldValue}>
                    {bulletin.participants.map((p) => formatParticipantLine(p)).join('\n')}
                  </p>
                </div>
              ) : null}
            </div>
            <CompactText className={screen.note} tone={'info'}>
              Бюджет - пожелание. Минимум 500 руб/час; одну границу достаточно. Цену задает
              преподаватель.
            </CompactText>
            <div className={screen.actions}>
              <button type='button' className={screen.ink} onClick={openEdit}>
                Редактировать
              </button>
              <button type='button' className={screen.ghost} onClick={() => setConfirmClose(true)}>
                Завершить задачу
              </button>
            </div>
          </section>

          <section className={screen.card}>
            <h2 className={screen.cardTitle}>Отклики</h2>
            <p className={screen.body}>
              {responseLines.length
                ? `${responseLines.length} ${plural(responseLines.length, 'отклик', 'отклика', 'откликов')} по этой задаче.`
                : 'Пока нет откликов. Репетиторы появятся в списке, когда откликнутся или ты отправишь приглашение.'}
            </p>
            {responseLines.length > 0 ? (
              <p className={screen.body} style={{ whiteSpace: 'pre-line' }}>
                {responseLines.join('\n')}
              </p>
            ) : null}
            <div className={screen.actions}>
              <button
                type='button'
                className={screen.ink}
                onClick={() => navigate(`/profile/details/${bulletin.id}/search`)}
              >
                Посмотреть репетиторов
              </button>
            </div>
          </section>

          {/* Условия чата - только то, что согласуется с репетитором (данные заявки выше не повторяем). */}
          {chatProposal ? (
            <section className={screen.card} data-chat-terms>
              <h2 className={screen.cardTitle}>Условия, согласованные в чате</h2>
              {chatTerms.length ? (
                <div className={screen.row}>
                  {chatTerms.map((term) => (
                    <div key={term.label} className={screen.field}>
                      <p className={screen.fieldLabel}>{term.label}</p>
                      <p className={screen.fieldValue}>{term.value}</p>
                    </div>
                  ))}
                </div>
              ) : null}
              <p className={screen.body}>
                Длительность урока, формат и точное время подтвердите в чате до обмена контактами.
              </p>
              <CompactText className={screen.note} tone={'info'}>
                Платформа не принимает оплату стоимости занятия. Способ оплаты обсуди с
                преподавателем.
              </CompactText>
              <div className={screen.actions}>
                <button
                  type='button'
                  className={screen.ink}
                  onClick={() => navigate(`/profile/details/${bulletin.id}/chats`)}
                >
                  Перейти в чат
                </button>
                {chatProposal.tutor_id ? (
                  <button
                    type='button'
                    className={screen.ghost}
                    onClick={() => navigate(`/profile/leave-review/${chatProposal.tutor_id}`)}
                  >
                    Написать отзыв
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}
        </>
      )}

      {wizardOpen ? (
        <TaskWizardModal
          open={wizardOpen}
          onClose={closeEdit}
          initialStep={0}
          title='Редактирование задачи'
        />
      ) : null}

      {editPending ? (
        <div className={screen.backdrop} role='presentation' onClick={() => setEditPending(null)}>
          <div
            className={screen.dialog}
            role='dialog'
            aria-labelledby='save-task-title'
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id='save-task-title' className={screen.dialogTitle}>
              Сохранить изменения?
            </h2>
            <p className={screen.body}>
              Условия задачи обновятся, репетиторы увидят новую версию.
            </p>
            <div className={screen.actions}>
              <button
                type='button'
                className={screen.ink}
                disabled={saveEdit.isPending}
                onClick={() => saveEdit.mutate(editPending)}
              >
                {saveEdit.isPending ? 'Сохраняем...' : 'Сохранить изменения'}
              </button>
              <button type='button' className={screen.ghost} onClick={() => setEditPending(null)}>
                Не сохранять
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmClose ? (
        <div className={screen.backdrop} role='presentation' onClick={() => setConfirmClose(false)}>
          <div
            className={screen.dialog}
            role='dialog'
            aria-labelledby='close-task-title'
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id='close-task-title' className={screen.dialogTitle}>
              Завершить задачу?
            </h2>
            <p className={screen.body}>
              {[subjectTitle, grades, locations].filter(Boolean).join(' / ')}
            </p>
            <p className={screen.body}>
              Ты завершаешь поиск по задаче. Это не подтверждение проведения или оплаты занятия.
              Договоренность проверь отдельно в чате.
            </p>
            <div className={screen.actions}>
              <button
                type='button'
                className={screen.ink}
                disabled={closeTask.isPending}
                onClick={() => closeTask.mutate()}
              >
                Подтвердить завершение
              </button>
              <button type='button' className={screen.ghost} onClick={() => setConfirmClose(false)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};
